import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/layout/DataTable";
import {
  knowledgeBaseApi,
  type KbTag,
  type KbTagKind,
} from "@/api/knowledgeBase";

// Knowledge Base v1.0 PR 4 — Tags admin. Single grid, kind-filtered.
// Mirrors AdminCategoriesPage / TagManagementPage UX.

const QUERY_KEY = ["kb", "admin", "tags"] as const;
const KINDS: KbTagKind[] = [
  "Audience",
  "DocumentType",
  "ParentAgreement",
  "Theme",
  "Lifecycle",
];

export function KnowledgeBaseTagsAdminPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("KnowledgeBase.ManageDocuments");
  const toast = useToast();
  const qc = useQueryClient();
  const [kindFilter, setKindFilter] = useState<KbTagKind | "">("");
  const [editTarget, setEditTarget] = useState<KbTag | "new" | null>(null);

  const listQ = useQuery({
    queryKey: [...QUERY_KEY, kindFilter || null],
    queryFn: () => knowledgeBaseApi.listTags(kindFilter || undefined),
    enabled: canManage,
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => knowledgeBaseApi.deleteTag(code),
    onSuccess: () => {
      toast.success("Tag deleted.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) =>
      toast.error(
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Delete failed.",
      ),
  });

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to manage knowledge base tags.
      </div>
    );
  }

  const columns: Column<KbTag>[] = [
    {
      key: "code",
      header: "Code",
      render: (t) => <span className="font-mono text-onyx font-semibold">{t.code}</span>,
    },
    { key: "label", header: "Label", render: (t) => <span className="text-onyx">{t.label}</span> },
    {
      key: "kind",
      header: "Kind",
      render: (t) => <span className="text-caption text-gold-shadow">{t.kind}</span>,
    },
    {
      key: "order",
      header: "Order",
      render: (t) => <span className="font-mono text-caption">{t.displayOrder}</span>,
    },
    {
      key: "actions",
      header: "",
      render: (t) => (
        <div className="flex items-center gap-space-1 justify-end">
          <button
            type="button"
            onClick={() => setEditTarget(t)}
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Edit ${t.code}`}
          >
            <Pencil size={16} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Delete tag '${t.label}'?`)) {
                deleteMutation.mutate(t.code);
              }
            }}
            disabled={deleteMutation.isPending}
            className="p-space-1 rounded-md text-gray-mid hover:text-status-error hover:bg-white-warm"
            aria-label={`Delete ${t.code}`}
          >
            <Trash2 size={16} aria-hidden />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin · Knowledge Base"
        title="Tags"
        description="Tags applied to documents. Grouped by kind (audience, document type, theme, lifecycle, parent agreement)."
        actions={
          <Button type="button" onClick={() => setEditTarget("new")}>
            <Plus size={16} aria-hidden /> New tag
          </Button>
        }
      />

      <div className="flex items-end gap-space-3 mb-space-4">
        <div>
          <label
            htmlFor="kb-tag-kind"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
          >
            Kind
          </label>
          <Select
            id="kb-tag-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KbTagKind | "")}
            className="w-48"
          >
            <option value="">All kinds</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={listQ.data ?? []}
        loading={listQ.isLoading}
        error={listQ.isError ? (listQ.error as Error).message : null}
        emptyMessage="No tags yet. Click 'New tag' to add one."
        rowKey={(t) => t.code}
      />

      {editTarget !== null && (
        <TagEditDialog
          target={editTarget}
          existingCodes={(listQ.data ?? []).map((t) => t.code)}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            qc.invalidateQueries({ queryKey: QUERY_KEY });
          }}
        />
      )}
    </>
  );
}

interface TagEditDialogProps {
  target: KbTag | "new";
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => void;
}

const TAG_CODE_REGEX = /^[A-Za-z0-9_-]{1,50}$/;

function TagEditDialog({ target, existingCodes, onClose, onSaved }: TagEditDialogProps) {
  const isNew = target === "new";
  const initial = isNew ? undefined : target;
  const toast = useToast();
  const [code, setCode] = useState(initial?.code ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [kind, setKind] = useState<KbTagKind>(initial?.kind ?? "Theme");
  const [displayOrder, setDisplayOrder] = useState(
    initial?.displayOrder !== undefined ? String(initial.displayOrder) : "0",
  );
  const [errors, setErrors] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: () =>
      knowledgeBaseApi.createTag({
        code: code.trim(),
        label: label.trim(),
        kind,
        displayOrder: parseInt(displayOrder, 10) || 0,
      }),
    onSuccess: () => {
      toast.success("Tag created.");
      onSaved();
    },
    onError: (err) => surface(err),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      initial
        ? knowledgeBaseApi.updateTag(initial.code, {
            label: label.trim(),
            kind,
            displayOrder: parseInt(displayOrder, 10) || 0,
          })
        : Promise.reject(new Error("No tag to update.")),
    onSuccess: () => {
      toast.success("Tag updated.");
      onSaved();
    },
    onError: (err) => surface(err),
  });

  const surface = (err: unknown) => {
    if (err instanceof ApiError && err.errors.length) setErrors(err.errors);
    else setErrors([err instanceof Error ? err.message : "Save failed."]);
  };

  const validate = (): string[] => {
    const issues: string[] = [];
    if (isNew) {
      const trimmed = code.trim();
      if (!trimmed) issues.push("Code is required.");
      else if (!TAG_CODE_REGEX.test(trimmed))
        issues.push("Code must be 1–50 letters, digits, hyphen, or underscore.");
      else if (existingCodes.some((c) => c.toLowerCase() === trimmed.toLowerCase()))
        issues.push(`Code '${trimmed}' already exists.`);
    }
    if (!label.trim()) issues.push("Label is required.");
    return issues;
  };

  const submit = () => {
    setErrors([]);
    const issues = validate();
    if (issues.length) {
      setErrors(issues);
      return;
    }
    if (isNew) createMutation.mutate();
    else updateMutation.mutate();
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isNew ? "New tag" : `Edit ${initial?.code}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={pending}>
            {isNew ? "Create tag" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField
          label="Code"
          required={isNew}
          htmlFor="kb-tag-code"
          helperText={
            isNew
              ? "Permanent identifier. Letters, digits, '-' or '_'."
              : "Codes are permanent."
          }
        >
          {isNew ? (
            <Input
              id="kb-tag-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={50}
              autoFocus
              className="font-mono"
            />
          ) : (
            <div className="font-mono text-onyx font-semibold px-space-2 py-[6px] bg-white-warm border border-border-light rounded-md inline-block">
              {initial?.code}
            </div>
          )}
        </FormField>

        <FormField label="Label" required htmlFor="kb-tag-label">
          <Input
            id="kb-tag-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={100}
            autoFocus={!isNew}
          />
        </FormField>

        <FormField label="Kind" required htmlFor="kb-tag-kind-input">
          <Select
            id="kb-tag-kind-input"
            value={kind}
            onChange={(e) => setKind(e.target.value as KbTagKind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Display order" htmlFor="kb-tag-order">
          <Input
            id="kb-tag-order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className="max-w-[8rem] font-mono"
          />
        </FormField>

        {errors.length > 0 && (
          <ul
            className="list-disc pl-space-4 text-caption text-status-error"
            role="alert"
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
